import React, { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  deletePartenaire,
} from "../../../services/partenaireskataServices";
import EditPartenaireModal from "./EditPartenaireModal";
import {
  FaCheckCircle,
  FaEdit,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaShieldAlt,
  FaTrash,
  FaUserShield,
} from "react-icons/fa";

const DEFAULT_IMAGE = "/img/default_image.jpg";

const gradeImages = {
  "1er Dan": "/images/belt/ceinture_judo_1D.png",
  "2ème Dan": "/images/belt/ceinture_judo_2D.png",
  "3ème Dan": "/images/belt/ceinture_judo_3D.png",
  "4ème Dan": "/images/belt/ceinture_judo_4D.png",
  "5ème Dan": "/images/belt/ceinture_judo_5D.png",
  "6ème Dan": "/images/belt/ceinture_judo_rouge_blanche.png",
  "7ème Dan": "/images/belt/ceinture_judo_rouge_blanche.png",
  "8ème Dan": "/images/belt/ceinture_judo_rouge.png",
  "9ème Dan": "/images/belt/ceinture_judo_rouge.png",
  "10ème Dan": "/images/belt/ceinture_judo_rouge.png",
};

function ActionIconButton({ icon, onClick, title, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl text-sm font-semibold transition ${className}`}
    >
      {icon}
    </button>
  );
}

export default function PartenaireKataCard({
  partenaire,
  refreshPartenairesList,
}) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [authError, setAuthError] = useState("");

  const sanitizedContent = useMemo(
    () => DOMPurify.sanitize(partenaire?.content || ""),
    [partenaire?.content]
  );

  const handleDeletePartenaire = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce partenaire ?")) {
      return;
    }

    try {
      await deletePartenaire(partenaire.id);
      refreshPartenairesList?.();
    } catch (error) {
      console.error("Erreur lors de la suppression du partenaire:", error);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();

    if (
      emailInput === partenaire.mail &&
      passwordInput === partenaire.password
    ) {
      setIsVerified(true);
      setShowAuthForm(false);
      setAuthError("");
    } else {
      setAuthError("Mail ou mot de passe incorrect.");
    }
  };

  const toggleAuthForm = () => {
    setShowAuthForm((prev) => !prev);
    setAuthError("");
  };

  const refreshList = () => {
    refreshPartenairesList?.();
  };

  const beltImage = gradeImages[partenaire.passage_grade] || DEFAULT_IMAGE;

  return (
    <>
      <article className="group flex h-full flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900">
        <div className="h-1.5 w-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white px-5 py-5 dark:border-slate-800 dark:from-slate-900 dark:to-slate-900">
          <div className="flex flex-col items-center text-center">
            <img
              src={beltImage}
              alt={partenaire.passage_grade}
              className="h-20 w-48 object-contain"
            />

            <div className="mt-3 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
              {partenaire.passage_grade}
            </div>

            <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
              {partenaire.nom} {partenaire.prenom}
            </h3>

            <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">
              {partenaire.club}
            </p>

            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {partenaire.departement}
              </span>
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                {partenaire.region}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Informations
            </p>

            {sanitizedContent ? (
              <div
                className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              />
            ) : (
              <p className="mt-3 text-sm italic text-slate-400">
                Aucune précision renseignée.
              </p>
            )}
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4 dark:border-slate-800">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {partenaire.tel ? (
                <ActionIconButton
                  title="Appeler"
                  onClick={() => window.open(`tel:${partenaire.tel}`, "_blank")}
                  icon={<FaPhone />}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                />
              ) : null}

              {partenaire.mail ? (
                <ActionIconButton
                  title="Envoyer un email"
                  onClick={() => window.open(`mailto:${partenaire.mail}`, "_blank")}
                  icon={<FaEnvelope />}
                  className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                />
              ) : null}

              {isVerified ? (
                <>
                  <ActionIconButton
                    title="Modifier l’annonce"
                    onClick={() => setEditModalOpen(true)}
                    icon={<FaEdit />}
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                  />

                  <ActionIconButton
                    title="Supprimer l’annonce"
                    onClick={handleDeletePartenaire}
                    icon={<FaTrash />}
                    className="bg-red-600 text-white hover:bg-red-700"
                  />
                </>
              ) : (
                <button
                  type="button"
                  onClick={toggleAuthForm}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaUserShield />
                  Vérifier mon annonce
                </button>
              )}
            </div>

            {showAuthForm && !isVerified && (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  <FaShieldAlt className="text-slate-500 dark:text-slate-400" />
                  Vérification dépositaire
                </div>

                <form onSubmit={handleAuthSubmit} className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="Votre email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                      Mot de passe
                    </label>
                    <input
                      type="password"
                      placeholder="Votre mot de passe"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      required
                    />
                  </div>

                  {authError ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                      {authError}
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    <FaCheckCircle />
                    Vérifier
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </article>

      <EditPartenaireModal
        partenaire={partenaire}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        refreshList={refreshList}
      />
    </>
  );
}